import { Fragment, type FunctionComponent, type ReactElement, useEffect } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { ShakeAnimation } from '~/ui/animations/shake/ShakeAnimation';
import { useShakeOnClickOutside } from '~/ui/animations/shake/use-shake-on-click-outside';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import {
    BoundCommentsSurfaceProvider,
    CommentsGroupMemberBinding,
    CommentsOpenRequestProvider,
} from '../../../../comments';
import { useUrlTileEscapeClose, useUrlTiles, withCommentsOpen } from '../../../../url-tiles';
import { Backdrop } from '../../../components/Common';
import { useJobArchitecturePermissions } from '../../../context/JobArchitecturePermissionsContext';
import { isJsaProfileDetailTilePayload, JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../../../url-tile-payloads';
import { useJobArchitectureUrlTileActions } from '../../../url-tiles/use-job-architecture-url-tile-actions';
import { useProfileTask } from '../../api/use-profile-task';
import { createTaskCommentsSubject } from '../../comments/profile-task-comments-adapter';
import { useProfileTaskCommentsApi } from '../../comments/profile-task-comments-api';
import {
    useProfileTaskDraftCoordinator,
    useProfileTaskParentDraft,
    useProfileTaskParentLanguage,
} from '../../draft/ProfileTaskDraftCoordinatorProvider';
import { resolveProfileTaskPermissions } from '../../profile-task-permissions';
import { normalizeProfileTaskTitle } from '../../validation/profile-task-validation';
import { CreateTaskTileContent } from '../CreateTaskTileContent';
import { DraftTaskUnavailable } from '../DraftTaskUnavailable';
import { ProfileTaskForm } from '../ProfileTaskForm';
import type { ProfileTaskTilePayload } from '../task-tile-url';
import { useProfileTaskNavigationGuard } from '../use-profile-task-navigation-guard';
import { PersistedTaskTileContent } from './PersistedTaskTileContent';
import { TaskTileError, TaskTileLoading } from './TaskTileFallbacks';
import { TaskTileFrame } from './TaskTileFrame';
import { TaskTileHeader } from './TaskTileHeader';

type ProfileTaskTileProps = {
    readonly onClose: () => void;
    readonly onRestorePayload: (payload: ProfileTaskTilePayload) => void;
    readonly payload: ProfileTaskTilePayload;
};

type GroupCommentsToggle = {
    readonly isOpen: boolean;
    readonly onToggle: () => void;
    readonly requestOpen: () => void;
};

// The rail is the group's. While the parent profile tile is open its payload owns the
// comments-open flag, so the task's toggle reads and writes that same flag and every member
// offers the same entry point. Opened on its own — deep link, skills or tasks page — the task
// hosts the rail itself and keeps the flag in its own payload.
const useGroupCommentsToggle = (
    payload: ProfileTaskTilePayload,
    onUpdatePayload: (payload: ProfileTaskTilePayload) => void,
): GroupCommentsToggle => {
    const { toggleJobProfileTileCommentsOpen, updateJobProfileTileCommentsOpen } = useJobArchitectureUrlTileActions();
    const { tiles } = useUrlTiles();
    const primaryPayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;
    const parentPayload =
        isJsaProfileDetailTilePayload(primaryPayload) &&
        primaryPayload.architectureId === payload.architectureId &&
        primaryPayload.profileId === payload.parentId
            ? primaryPayload
            : undefined;

    if (parentPayload !== undefined) {
        return {
            isOpen: parentPayload.isCommentsOpen === true,
            onToggle: toggleJobProfileTileCommentsOpen,
            requestOpen: () => updateJobProfileTileCommentsOpen(true),
        };
    }

    const isOpen = payload.isCommentsOpen === true;

    return {
        isOpen,
        onToggle: () => onUpdatePayload(withCommentsOpen(payload, !isOpen)),
        requestOpen: () => {
            if (!isOpen) {
                onUpdatePayload(withCommentsOpen(payload, true));
            }
        },
    };
};

export const ProfileTaskTile: FunctionComponent<ProfileTaskTileProps> = ({ onClose, onRestorePayload, payload }) => {
    const { canRead: parentCanRead } = useJobArchitecturePermissions();
    const commentsApi = useProfileTaskCommentsApi();
    const commentsToggle = useGroupCommentsToggle(payload, onRestorePayload);
    const coordinator = useProfileTaskDraftCoordinator();
    const { isShaking, shake } = useShakeOnClickOutside();
    const parentContext = {
        architectureId: payload.architectureId,
        parentId: payload.parentId,
        parentType: payload.parentType,
        parentVersion: payload.parentVersion,
        roleId: payload.roleId,
    } as const;
    const navigationGuard = useProfileTaskNavigationGuard({
        context: parentContext,
        onClose,
        onRestorePayload,
        payload,
        taskId: payload.taskId,
    });
    useUrlTileEscapeClose(navigationGuard.requestClose);
    const parentDraft = useProfileTaskParentDraft(parentContext);
    const parentLanguage = useProfileTaskParentLanguage(parentContext);

    useEffect(() => {
        if (parentLanguage !== undefined && payload.language !== parentLanguage) {
            onRestorePayload({ ...payload, language: parentLanguage });
        }
    }, [onRestorePayload, parentLanguage, payload]);

    const activeLanguage = payload.language ?? parentLanguage ?? 'en-US';
    const query = useProfileTask({
        architectureId: payload.architectureId,
        enabled: payload.draft !== true,
        language: activeLanguage,
        parentId: payload.parentId,
        parentType: payload.parentType,
        parentVersion: payload.parentVersion,
        taskId: payload.taskId,
        taskVersionId: payload.draft === true ? '' : payload.taskVersionId,
    });
    const translate = useTranslate();
    const discardDialog = (
        <ConfirmationDialog
            cancelButtonLabel={translate('common.cancel')}
            confirmButtonLabel={translate('common.confirm')}
            confirmButtonVariant="default"
            description={translate('dialogs.discard.content')}
            open={navigationGuard.isDiscardDialogOpen}
            title={translate('dialogs.discard.title')}
            onCancel={navigationGuard.cancelDiscard}
            onConfirm={navigationGuard.confirmDiscard}
        />
    );

    // Edit mode dims the role view behind the tile and vibrates the tile when the user clicks it,
    // mirroring the role tile treatment in `job-profile-section/Details.tsx`.
    const isEditing = payload.draft === true || parentDraft !== undefined;
    const withEditModeSurface = (content: ReactElement): ReactElement =>
        isEditing ? (
            <Fragment>
                <Backdrop onClick={shake} />
                <ShakeAnimation isShaking={isShaking}>{content}</ShakeAnimation>
            </Fragment>
        ) : (
            content
        );

    const applyChanges = (): void => {
        // Rejected when the buffered form is invalid; the form already renders the inline messages,
        // so the shake is the only extra signal the header needs.
        if (!coordinator.getState().applyEditor(parentContext, payload.taskId)) {
            shake();
            return;
        }

        // A successful apply leaves the editor clean, so this closes without the discard dialog.
        navigationGuard.requestClose();
    };
    const editorState = parentDraft?.editors.get(payload.taskId);
    const isTitleMissing = editorState !== undefined && normalizeProfileTaskTitle(editorState.form.title).length === 0;
    const applyButton = (
        <Button
            data-testid="profile-task-apply-changes"
            disabled={editorState?.isDirty !== true || isTitleMissing}
            size="sm"
            variant="primary"
            onClick={applyChanges}
        >
            {translate('pages.job-architecture-v2.profile-tasks.apply-changes')}
        </Button>
    );
    const renderEditHeader = (): ReactElement => (
        <TaskTileHeader
            actions={
                isTitleMissing ? (
                    <Tooltip>
                        {/* Disabled buttons swallow pointer events, so the tooltip listens on a wrapper. */}
                        <Tooltip.Trigger asChild={true}>
                            <span className="inline-flex">{applyButton}</span>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content side="bottom">
                                {translate('pages.job-architecture-v2.profile-tasks.validation.title')}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip>
                ) : (
                    applyButton
                )
            }
            isSticky={false}
            onClose={navigationGuard.requestClose}
        />
    );

    if (payload.draft === true) {
        return withEditModeSurface(
            <Tile>
                {discardDialog}
                <ScrollArea scrollFade="vertical" scrollFadeSize="var(--spacing-4xl)">
                    <TileContent className="min-h-full w-full">
                        {parentDraft?.editors.has(payload.taskId) === true ? (
                            <CreateTaskTileContent
                                context={parentContext}
                                language={payload.language ?? parentLanguage ?? 'en-US'}
                                taskId={payload.taskId}
                                onRequestClose={navigationGuard.requestClose}
                            />
                        ) : (
                            <div className="flex w-full flex-col gap-4xl">
                                <TaskTileHeader onClose={navigationGuard.requestClose} />
                                <DraftTaskUnavailable onClose={onClose} />
                            </div>
                        )}
                    </TileContent>
                </ScrollArea>
            </Tile>,
        );
    }

    if (query.isLoading) {
        return withEditModeSurface(
            <Fragment>
                {discardDialog}
                <TaskTileFrame testId="profile-task-loading" onClose={navigationGuard.requestClose}>
                    <TaskTileLoading />
                </TaskTileFrame>
            </Fragment>,
        );
    }

    if (query.isError || query.data === undefined) {
        return withEditModeSurface(
            <Fragment>
                {discardDialog}
                <TaskTileFrame onClose={navigationGuard.requestClose}>
                    <TaskTileError />
                </TaskTileFrame>
            </Fragment>,
        );
    }

    const task = query.data;
    const taskDraftReference = parentDraft?.applied.find(({ taskId }) => taskId === payload.taskId);
    const hasStagedChanges =
        taskDraftReference?.draft !== undefined || parentDraft?.editors.get(payload.taskId)?.isDirty === true;
    const taskPermissions = resolveProfileTaskPermissions({
        hasStagedChanges,
        isParentEditing: parentDraft !== undefined,
        isPersisted: true,
        parentCanEdit: parentDraft !== undefined,
        parentCanRead,
    });
    const subject = createTaskCommentsSubject({
        architectureId: payload.architectureId,
        canComment: taskPermissions.canComment,
        hasStagedChanges,
        language: activeLanguage,
        taskId: task.taskId,
        taskVersionId: task.taskVersionId,
    });
    if (parentDraft === undefined) {
        return (
            <BoundCommentsSurfaceProvider api={commentsApi} subject={subject}>
                <CommentsOpenRequestProvider requestCommentsOpen={commentsToggle.requestOpen}>
                    <CommentsGroupMemberBinding
                        api={commentsApi}
                        label={task.title}
                        memberRole="task"
                        subject={subject}
                    />
                    {discardDialog}
                    <PersistedTaskTileContent
                        activeLanguage={activeLanguage}
                        automation={query.automation}
                        canComment={taskPermissions.canComment}
                        comments={commentsToggle}
                        context={parentContext}
                        task={task}
                        onClose={navigationGuard.requestClose}
                    />
                </CommentsOpenRequestProvider>
            </BoundCommentsSurfaceProvider>
        );
    }

    if (task.generationState !== 'finished') {
        return withEditModeSurface(
            <Fragment>
                {discardDialog}
                <PersistedTaskTileContent
                    activeLanguage={activeLanguage}
                    automation={query.automation}
                    canComment={false}
                    context={parentContext}
                    task={task}
                    onClose={navigationGuard.requestClose}
                />
            </Fragment>,
        );
    }

    return withEditModeSurface(
        <Tile data-testid="profile-task-detail">
            {discardDialog}
            <ScrollArea
                scrollFade="vertical"
                scrollFadeSize="var(--spacing-4xl)"
                viewportTestId="profile-task-scroll-viewport"
            >
                <TileContent className="min-h-full w-full">
                    <ProfileTaskForm
                        activeLanguage={activeLanguage}
                        context={parentContext}
                        renderHeader={renderEditHeader}
                        task={task}
                        taskId={payload.taskId}
                    />
                </TileContent>
            </ScrollArea>
        </Tile>,
    );
};
